import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import StarRating from '../components/StarRating';
import useAuthStore from '../store/useAuthStore';
import useThemeStore from '../store/useThemeStore';
import useLangStore from '../store/useLangStore';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';
import api from '../services/api';
import useToastStore from '../store/useToastStore';
import ImageCropModal from '../components/ImageCropModal';

const AVATAR_EMOJIS = [
  '😎','🤩','🥳','😊','🧑‍🎨','👨‍💻','👩‍💻','🧑‍🚀','👨‍🎤','👩‍🎤',
  '🦁','🐯','🦊','🐺','🐻','🐼','🐨','🦝','🐸','🦋',
  '🌟','⚡','🔥','💎','🎯','🎨','🎬','📸','🎵','🚀',
];

const GCC_LOCATIONS = [
  { country: 'Kuwait', cities: ['Kuwait City', 'Salmiya', 'Hawalli', 'Farwaniya', 'Ahmadi', 'Jahra'] },
  { country: 'Saudi Arabia', cities: ['Riyadh', 'Jeddah', 'Dammam', 'Khobar', 'Mecca', 'Medina'] },
  { country: 'UAE', cities: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'RAK', 'Fujairah'] },
  { country: 'Qatar', cities: ['Doha', 'Al Wakrah', 'Al Khor', 'Lusail'] },
  { country: 'Bahrain', cities: ['Manama', 'Riffa', 'Muharraq', 'Hamad Town'] },
  { country: 'Oman', cities: ['Muscat', 'Salalah', 'Sohar', 'Nizwa'] },
];

// Map currency code → default country
const CURRENCY_COUNTRY = {
  KWD: 'Kuwait City', SAR: 'Riyadh', AED: 'Dubai',
  QAR: 'Doha', BHD: 'Manama', OMR: 'Muscat'
};

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const { lang, toggleLang } = useLangStore();
  const { currency, currency: detectedCurrency } = useCurrencyStore();
  const [editing, setEditing] = useState(false);
  const defaultLocation = user?.location || CURRENCY_COUNTRY[detectedCurrency?.code] || 'Kuwait City';
  const [form, setForm] = useState({
    fullName: user?.fullName || '',
    bio: user?.bio || '',
    location: defaultLocation,
    companyName: user?.companyName || '',
    hourlyRate: user?.hourlyRate || '',
    portfolioUrl: user?.portfolioUrl || ''
  });
  const [saving, setSaving] = useState(false);
  const [referral, setReferral] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const avatarInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const [cropModal, setCropModal] = useState(null); // { src, aspect, type }
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  useEffect(() => {
    api.get('/users/me/referrals').then(r => setReferral(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/users/me', form);
      updateUser(data);
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const src = URL.createObjectURL(file);
    setCropModal({ src, aspect: 1, type: 'avatar' });
    e.target.value = '';
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const src = URL.createObjectURL(file);
    setCropModal({ src, aspect: 16 / 9, type: 'banner' });
    e.target.value = '';
  };

  const handleCropDone = useCallback(async (blob) => {
    const type = cropModal?.type;
    setCropModal(null);
    if (!type) return;
    const formData = new FormData();
    formData.append(type === 'avatar' ? 'avatar' : 'banner', blob, `${type}.jpg`);
    try {
      const { data } = await api.put('/users/me', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateUser(data);
      useToastStore.getState().addToast(type === 'banner' ? 'Banner updated!' : 'Photo updated!', 'success');
    } catch (err) {
      useToastStore.getState().addToast(err?.response?.data?.error || 'Upload failed', 'error');
    }
  }, [cropModal, updateUser]);

  const handleEmojiAvatar = async (emoji) => {
    setShowEmojiPicker(false);
    // Render emoji to canvas → upload as avatar image
    const canvas = document.createElement('canvas');
    canvas.width = 200; canvas.height = 200;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1E1E2E';
    ctx.fillRect(0, 0, 200, 200);
    ctx.font = '120px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(emoji, 100, 110);
    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append('avatar', blob, 'avatar.png');
      try {
        const { data } = await api.put('/users/me', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        updateUser(data);
      } catch {}
    }, 'image/png');
  };

  const menuItems = [
    { icon: '📋', label: 'My Orders', action: () => navigate('/bookings') },
    { icon: '❤️', label: 'Saved Services', action: () => navigate('/favorites') },
    { icon: '💬', label: 'Messages', action: () => navigate('/inbox') },
    { icon: '⭐', label: 'My Reviews', action: () => navigate('/my-reviews') },
    ...(['PROVIDER', 'ADMIN'].includes(user?.accountType) ? [{ icon: '🛠️', label: 'My Listings', action: () => navigate('/my-services') }] : []),
    ...(user?.accountType === 'PROVIDER' ? [{ icon: '💰', label: 'My Earnings', action: () => navigate('/earnings') }] : []),
    ...(user?.accountType === 'PROVIDER' && !user?.isVerified ? [{ icon: '✅', label: 'Get Verified', action: () => navigate('/verification') }] : []),
    { icon: '🔔', label: 'Notifications', action: () => navigate('/notifications') },
    { icon: '🔒', label: 'Privacy & Security', action: () => navigate('/privacy') },
    { icon: '💳', label: 'Payment Methods', action: () => navigate('/wallet') },
    { icon: '❓', label: 'Help & Support', action: () => navigate('/help') },
    { icon: '📄', label: 'Terms & Privacy', action: () => navigate('/privacy') },
    ...(user?.accountType === 'ADMIN' ? [{ icon: '⚙️', label: 'Admin Dashboard', action: () => navigate('/admin') }] : []),
  ];

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      <div className="px-4 pt-12 pb-4">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>Account</h1>
          <Logo size="sm" showWordmark={false} />
        </div>

        {/* Hidden file inputs */}
        <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
        <input ref={bannerInputRef} type="file" accept="image/*" className="hidden" onChange={handleBannerChange} />

        {/* Profile card — banner + avatar */}
        <div className="bg-dark-surface border border-dark-border rounded-2xl overflow-hidden mb-6">

          {/* Banner */}
          <div className="relative h-28">
            {user?.bannerUrl ? (
              <img src={user.bannerUrl} className="w-full h-full object-cover" alt="" />
            ) : (
              <div className="w-full h-full" style={{ background: 'linear-gradient(135deg, #6C5CE7 0%, #a855f7 50%, #ec4899 100%)' }} />
            )}
            {(user?.accountType === 'PROVIDER' || user?.accountType === 'ADMIN') && (
              <button
                onClick={() => bannerInputRef.current?.click()}
                className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/40 backdrop-blur text-white text-xs font-medium"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
                Edit banner
              </button>
            )}
          </div>

          {/* Avatar row */}
          <div className="px-4 -mt-10 mb-3 flex items-end justify-between">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl border-4 border-dark-surface bg-brand-gradient flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                {user?.avatar
                  ? <img src={user.avatar} className="w-full h-full object-cover" alt="" />
                  : user?.fullName?.[0]}
              </div>
              {/* Camera icon — opens choice: photo or emoji */}
              <button
                onClick={() => setShowEmojiPicker(v => !v)}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-purple flex items-center justify-center shadow-lg"
                aria-label="Change photo"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                  <circle cx="12" cy="13" r="4"/>
                </svg>
              </button>
              {/* Avatar picker dropdown */}
              {showEmojiPicker && (
                <div className="absolute left-0 top-full mt-2 z-30 bg-dark-surface border border-dark-border rounded-2xl shadow-xl w-72 p-3">
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => { setShowEmojiPicker(false); avatarInputRef.current?.click(); }}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-dark-bg border border-dark-border text-dark-text text-xs font-medium"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                      Upload photo
                    </button>
                    <button
                      onClick={() => setShowEmojiPicker(false)}
                      className="w-9 flex items-center justify-center rounded-xl bg-dark-bg border border-dark-border text-dark-subtext"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                  <p className="text-dark-subtext text-xs mb-2 font-medium">Or pick an emoji</p>
                  <div className="grid grid-cols-6 gap-1">
                    {AVATAR_EMOJIS.map(em => (
                      <button
                        key={em}
                        onClick={() => handleEmojiAvatar(em)}
                        className="text-2xl w-10 h-10 rounded-xl hover:bg-dark-muted flex items-center justify-center transition-colors"
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setEditing(v => !v)}
              className="mb-1 px-4 py-2 rounded-xl bg-dark-bg border border-dark-border text-dark-text text-xs font-semibold"
            >
              {editing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>

          {/* Name + info */}
          <div className="px-4 pb-4">
            {editing ? (
              <input
                value={form.fullName}
                onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
                className="bg-dark-bg border border-dark-border rounded-lg px-3 py-1.5 text-dark-text text-sm outline-none focus:border-brand-purple w-full mb-2 font-bold"
              />
            ) : (
              <h2 className="text-dark-text font-bold text-xl leading-tight mb-0.5">{user?.fullName}</h2>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-dark-subtext text-sm">{user?.email}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                user?.accountType === 'PROVIDER' ? 'bg-brand-purple/20 text-brand-purple' : 'bg-blue-500/20 text-blue-400'
              }`}>{user?.accountType}</span>
              {user?.isVerified && (
                <span className="flex items-center gap-1 text-xs text-green-400">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                  Verified
                </span>
              )}
              {user?.location && <span className="text-dark-subtext text-xs">{user.location}</span>}
            </div>
            {user?.bio && !editing && (
              <p className="text-dark-subtext text-sm mt-2 leading-relaxed">{user.bio}</p>
            )}
            {user?.portfolioUrl && !editing && (
              <a
                href={user.portfolioUrl.startsWith('http') ? user.portfolioUrl : `https://${user.portfolioUrl}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-brand-purple text-xs font-medium mt-1 hover:underline"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                </svg>
                Portfolio
              </a>
            )}

            {/* Stats for providers */}
            {user?.accountType === 'PROVIDER' && (
              <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-dark-border">
                <div className="text-center">
                  <p className="text-dark-text font-bold text-base">{user?.reviewCount || 0}</p>
                  <p className="text-dark-subtext text-xs">Reviews</p>
                </div>
                <div className="text-center">
                  <div className="flex justify-center mb-0.5">
                    <StarRating rating={user?.rating || 0} size={12} />
                  </div>
                  <p className="text-dark-subtext text-xs">{(user?.rating || 0).toFixed(1)} Rating</p>
                </div>
                <div className="text-center">
                  <p className="text-dark-text font-bold text-base">
                    {user?.hourlyRate ? formatPrice(user.hourlyRate, currency) : '—'}
                  </p>
                  <p className="text-dark-subtext text-xs">Hourly rate</p>
                </div>
              </div>
            )}

            {/* Edit fields */}
            {editing && (
              <div className="mt-3 flex flex-col gap-3">
                <textarea
                  placeholder="Bio — tell clients about yourself..."
                  value={form.bio}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  rows={3}
                  className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple w-full resize-none"
                />
                <div className="relative">
                  <select
                    value={form.location}
                    onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple w-full appearance-none"
                  >
                    <option value="">Select city...</option>
                    {GCC_LOCATIONS.map(g => (
                      <optgroup key={g.country} label={g.country}>
                        {g.cities.map(c => <option key={c} value={c}>{c}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B6B7A" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                </div>
                {user?.accountType === 'PROVIDER' && (
                  <>
                    <input
                      placeholder="Company / Studio name"
                      value={form.companyName}
                      onChange={e => setForm(f => ({ ...f, companyName: e.target.value }))}
                      className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple w-full"
                    />
                    <input
                      type="number"
                      placeholder={`Hourly rate (${currency?.code || 'KWD'})`}
                      value={form.hourlyRate}
                      onChange={e => setForm(f => ({ ...f, hourlyRate: e.target.value }))}
                      className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple w-full"
                    />
                    <input
                      placeholder="Portfolio URL (e.g. behance.net/yourname)"
                      value={form.portfolioUrl}
                      onChange={e => setForm(f => ({ ...f, portfolioUrl: e.target.value }))}
                      className="bg-dark-bg border border-dark-border rounded-lg px-3 py-2 text-dark-text text-sm outline-none focus:border-brand-purple w-full"
                    />
                  </>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full py-2.5 rounded-xl bg-brand-gradient text-white text-sm font-semibold disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            {/* Profile completion bar */}
            {(() => {
              const fields = [user?.avatar, user?.bio, user?.location, user?.fullName];
              const extra = user?.accountType === 'PROVIDER' ? [user?.companyName || user?.hourlyRate] : [];
              const all = [...fields, ...extra];
              const done = all.filter(Boolean).length;
              const pct = Math.round((done / all.length) * 100);
              if (pct === 100 || editing) return null;
              return (
                <div className="mt-3 pt-3 border-t border-dark-border">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-dark-subtext">Profile completion</p>
                    <p className="text-xs font-semibold text-brand-purple">{pct}%</p>
                  </div>
                  <div className="h-1.5 bg-dark-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #6C5CE7, #a855f7)' }} />
                  </div>
                </div>
              );
            })()}
          </div>
        </div>


        {/* Menu items */}
        <div className="bg-dark-surface border border-dark-border rounded-2xl overflow-hidden mb-4">
          {menuItems.map((item, i) => (
            <button
              key={i}
              onClick={item.action}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left border-b border-dark-border last:border-b-0 hover:bg-dark-muted/30 transition-colors"
            >
              <span className="text-lg w-7 text-center">{item.icon}</span>
              <span className="flex-1 text-dark-text text-sm font-medium">{item.label}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          ))}

          {/* Dark / Light mode toggle */}
          <div className="w-full flex items-center gap-3 px-4 py-3.5 border-t border-dark-border">
            <span className="text-lg w-7 text-center">{theme === 'dark' ? '🌙' : '☀️'}</span>
            <span className="flex-1 text-dark-text text-sm font-medium">
              {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
            </span>
            {/* Toggle switch */}
            <button
              onClick={toggleTheme}
              className={`relative flex items-center w-12 h-6 rounded-full px-0.5 transition-colors duration-200 ${
                theme === 'dark' ? 'bg-brand-purple' : 'bg-dark-muted'
              }`}
            >
              <span
                className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                  theme === 'light' ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Arabic / English language toggle */}
          <div className="w-full flex items-center gap-3 px-4 py-3.5 border-t border-dark-border">
            <span className="text-lg w-7 text-center">{lang === 'ar' ? '🇸🇦' : '🇬🇧'}</span>
            <span className="flex-1 text-dark-text text-sm font-medium">English / عربي</span>
            <button
              onClick={() => { toggleLang(); useToastStore.getState().addToast('Arabic support coming soon!', 'info'); }}
              className={`relative flex items-center w-12 h-6 rounded-full px-0.5 transition-colors duration-200 ${
                lang === 'ar' ? 'bg-brand-purple' : 'bg-dark-muted'
              }`}
            >
              <span
                className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                  lang === 'ar' ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Logout */}
        <button
          onClick={async () => { await logout(); navigate('/'); }}
          className="w-full py-4 rounded-2xl bg-dark-surface border border-red-500/30 text-red-400 font-semibold text-sm mb-3"
        >
          Sign Out
        </button>

        {/* Delete account */}
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full py-3 rounded-2xl text-dark-subtext text-xs underline underline-offset-2"
        >
          Delete Account
        </button>

        <div className="flex flex-col items-center gap-1.5 mt-4">
          <a
            href="https://www.instagram.com/joinfindr"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-dark-subtext text-xs hover:text-brand-purple transition-colors"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
            @joinfindr
          </a>
          <p className="text-dark-subtext text-xs">Findr v1.0</p>
        </div>
      </div>

      {/* Delete account confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60 backdrop-blur-sm px-4 pb-10">
          <div className="w-full max-w-sm bg-dark-surface border border-dark-border rounded-3xl p-6">
            <div className="text-center mb-5">
              <span className="text-4xl block mb-3">⚠️</span>
              <h3 className="text-dark-text font-bold text-lg mb-1">Delete Account?</h3>
              <p className="text-dark-subtext text-sm leading-relaxed">
                This will permanently delete your account, listings, bookings, and all data. This cannot be undone.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await api.delete('/users/me');
                    await logout();
                    navigate('/');
                  } catch {
                    useToastStore.getState().addToast('Failed to delete account', 'error');
                    setDeleting(false);
                    setShowDeleteConfirm(false);
                  }
                }}
                disabled={deleting}
                className="w-full py-3.5 rounded-2xl bg-red-500 text-white font-semibold text-sm disabled:opacity-60"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full py-3.5 rounded-2xl bg-dark-bg border border-dark-border text-dark-text font-semibold text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tap-outside to close emoji picker */}
      {showEmojiPicker && (
        <div className="fixed inset-0 z-20" onClick={() => setShowEmojiPicker(false)} />
      )}

      {/* Image crop modal */}
      {cropModal && (
        <ImageCropModal
          src={cropModal.src}
          aspect={cropModal.aspect}
          onDone={handleCropDone}
          onClose={() => setCropModal(null)}
        />
      )}

      {/* Floating "Create Listing" button for PROVIDER accounts */}
      {['PROVIDER', 'ADMIN'].includes(user?.accountType) && (
        <button
          onClick={() => navigate('/create-service')}
          className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full bg-brand-gradient flex items-center justify-center shadow-lg shadow-brand-purple/30"
          aria-label="Create listing"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
      )}
    </div>
  );
}
